import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import RowSection from '../atoms/RowSection';
import styled from 'styled-components';
import { FormInstance } from 'antd/lib/form';
import { BankingData } from '../../interfaces/BankingData';
import jsonPaymentMethod from '../../mocks/paymentMethod.json';
import personalDataRepo from '../../services/repository/personalData';
import { Bank } from '../../interfaces/Bank';
import AdmissionFormItem from '../molecules/AdmissionFormItem';
import { AdmissionDataRules } from '../../services/util/field-rules-manager/types/fieldRuleTypes';
import { ProfessionalDocumentsRequest } from '../../interfaces/ProfessionalDocumentsRequest';
import { events } from '../../services/util/field-rules-manager/FieldRuleService';
import AdmissionMetadata from '../../types/AdmissionMetadata';
import { ProfessionalIdentificationTypes } from '../../constants/ProfessionalIdentificationTypes';
import { isScreenUnderWidth } from '../../services/util/screen';

const { Option } = Select;

const ButtonCol = styled(Col)`
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
`;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

type Props = {
  handleNext: () => void,
  initForm: BankingData,
  admissionDataRules: AdmissionDataRules,
  admissionMetadata: AdmissionMetadata
};

const BankDetails = React.forwardRef<FormInstance, Props>(({
  handleNext,
  initForm,
  admissionDataRules,
  admissionMetadata,
}, ref) => {
  const [form] = Form.useForm();
  const formRules = admissionDataRules.banking;
  // @ts-ignore
  useImperativeHandle(ref, () => ({
    validateFields: () => form.validateFields(),
    getFieldsValue: form.getFieldsValue,
    submit: () => form.submit(),
  }));

  useEffect(() => {
    form.setFieldsValue(initForm);
  }, [initForm]);

  const [data, setData] = useState<{
    banks: Bank[]
  }>({ banks: [] });

  const fetchData = async () => {
    try {
      const [banksResponse] = await Promise.all([
        personalDataRepo.findAllBanks(),
      ]);

      setData({
        banks: banksResponse.data,
      });
    } catch (error) {
      console.error('Ocorreu um erro ao buscar os dados:', error);
    }
  };

  useEffect(() => {
    fetchData().then(r => r);
  }, []);

  const onValuesChangeHandler = (changedValues: any, values: ProfessionalDocumentsRequest | undefined) => {
    const watchedFields = [
      formRules.paymentMethod?.name,
      formRules.paymentMethodComplementaryRemuneration?.name,
    ];

    if (Object.keys(changedValues).some(changedField => watchedFields.includes(changedField))) {
      events.triggerFormRuleUpdateEvent();
    }
  };

  const isUnder = isScreenUnderWidth(576);

  return (
    <>
      <Form form={form} {...formItemLayout} style={{ maxWidth: 600 }} requiredMark={false} layout='vertical'
        onValuesChange={onValuesChangeHandler}>
        <Row><RowSection>Dados para pagamento da sua remuneração</RowSection></Row>

        <Row gutter={16}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.paymentMethod}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={isUnder}
              >
                {jsonPaymentMethod.map((payment) => (
                  <Option key={`payment-method-${payment.id}`} value={payment.id}>
                    {payment.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={16} lg={16}>
            <AdmissionFormItem fieldRule={formRules.bank}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {data.banks.map((bank) => (
                  <Option key={`bank-${bank.id}`} value={bank.id}>
                    {bank.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>
        </Row>


        <Row gutter={16}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.agency}>
              <Input />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.account}>
              <Input />
            </AdmissionFormItem>
          </Col>
        </Row>

        {admissionMetadata.professionalIdentification === ProfessionalIdentificationTypes.ESTRATEGICO && (
          <>
            <Row><RowSection>Dados para pagamento da folha complementar</RowSection></Row>

            <Row gutter={16}>
              <Col xs={24} sm={24} md={8} lg={8}>
                <AdmissionFormItem fieldRule={formRules.paymentMethodComplementaryRemuneration}>
                  <Select
                    showSearch
                    optionFilterProp='children'
                    allowClear
                    filterOption={(input, option) => (
                      option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    )}
                    popupMatchSelectWidth={isUnder}
                  >
                    {jsonPaymentMethod.map((payment) => (
                      <Option key={`payment-method-${payment.id}`} value={payment.id}>
                        {payment.description}
                      </Option>
                    ))}
                  </Select>
                </AdmissionFormItem>
              </Col>
              <Col xs={24} sm={24} md={16} lg={16}>
                <AdmissionFormItem fieldRule={formRules.bankComplementaryRemuneration}>
                  <Select
                    showSearch
                    optionFilterProp='children'
                    allowClear
                    filterOption={(input, option) => (
                      option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    )}
                    popupMatchSelectWidth={false}
                  >
                    {data.banks.map((bank) => (
                      <Option key={`bankSupplementaryRemuneration-${bank.id}`} value={bank.id}>
                        {bank.description}
                      </Option>
                    ))}
                  </Select>
                </AdmissionFormItem>

              </Col>
            </Row>


            <Row gutter={16}>
              <Col xs={24} sm={24} md={8} lg={8}>
                <AdmissionFormItem fieldRule={formRules.complementaryAgency}>
                  <Input />
                </AdmissionFormItem>
              </Col>

              <Col xs={24} sm={24} md={8} lg={8}>
                <AdmissionFormItem fieldRule={formRules.complementaryAccount}>
                  <Input />
                </AdmissionFormItem>
              </Col>
            </Row>
          </>
        )}

      </Form>


      <Row>
        <ButtonCol span={24}> <Button type={'primary'}
          onClick={() => handleNext()}>Prosseguir</Button></ButtonCol>
      </Row>
    </>
  );
});

BankDetails.displayName = 'BankDetails';

export default BankDetails;
